import type { Models } from '@luxuryescapes/lib-flights-types'
import type { Fare, FlightSelectParams, AdditionalSelectFlightsPayload, Price } from 'api/flights'
import { selectflights } from 'api/flights'
import { mapAddedBaggageToPaxMap } from 'checkout/lib/utils/flights/extras'
import { getOtherFees } from 'checkout/lib/utils/flights/fees'
import { applyTravelFusionComplementaryReducer, buildBaggageMapFromFares } from 'checkout/lib/utils/flights/finalFares'
import { getFeederOriginAirportCode } from 'checkout/selectors/view/flights'
import { isOneWayFlight } from 'lib/flights/flightUtils'
import { isEmptyObject } from 'lib/object/objectUtils'
import type { AppDispatch } from '../store'
import { API_CALL, CHECKOUT_UPDATE_FLIGHT_ITEM_PAX_BAGGAGE, CHECKOUT_UPDATE_FLIGHT_TOTAL_FARE } from './actionConstants'
import type { AppAction } from './types/ActionTypes'
import { FETCH_FINAL_FLIGHT_FARES } from './apiActionConstants'
import config from 'constants/config'

function getSearchFnfEligible(state: App.State, searchId: string | undefined): boolean {
  if (!searchId) {
    return false
  }

  return Object.values(state.flights.searchV2Flights).some(search => {
    return search?.searchId === searchId && search?.fnfEligible === true
  })
}

function getEffectiveSelectedFareTotal(
  fare: Fare | undefined,
  fnfEligible: boolean,
  requiredSeats?: number,
): number {
  const effectiveSelectedFarePrice = getEffectiveSelectedFarePrice(fare, fnfEligible, requiredSeats)
  return Number(effectiveSelectedFarePrice?.all?.totalFareOriginalCurrency ?? effectiveSelectedFarePrice?.all?.totalFare ?? 0)
}

function isSelectedFareFnfPriceActive(
  fare: Fare | App.JourneyV2 | undefined,
  fnfEligible: boolean,
  requiredSeats?: number,
): boolean {
  if (!config.FLIGHTS_CHARTER_FRIENDS_AND_FAMILY_ENABLED) return false

  if (!fare) {
    return false
  }

  const flights = 'flightGroups' in fare
    ? fare.flightGroups.flatMap(group => group.flights)
    : fare.flightGroup.flights

  const minFnfSeats = Math.min(
    ...flights.map((flight: { fnfSeatsLeft?: number }) => flight.fnfSeatsLeft ?? 0),
  )
  const hasSufficientFnfInventory = requiredSeats != null && requiredSeats > 0
    ? minFnfSeats >= requiredSeats
    : minFnfSeats > 0

  return !!(fnfEligible && fare.fnfPrice && hasSufficientFnfInventory)
}

function getEffectiveSelectedFarePrice(
  fare: Fare | undefined,
  fnfEligible: boolean,
  requiredSeats?: number,
): Fare['price'] | Fare['fnfPrice'] | undefined {
  if (!fare) {
    return undefined
  }

  if (isSelectedFareFnfPriceActive(fare, fnfEligible, requiredSeats)) {
    return fare.fnfPrice
  }

  return fare.price
}

function getFareFamilyCostBreakdown(
  fare: Fare | undefined,
  fnfEligible: boolean,
  requiredSeats?: number,
): App.FlightFareFamily['cost'] | undefined {
  const effectiveSelectedFarePrice = getEffectiveSelectedFarePrice(fare, fnfEligible, requiredSeats)
  if (!effectiveSelectedFarePrice) {
    return undefined
  }

  return {
    adult: Number(effectiveSelectedFarePrice.adult?.totalFareOriginalCurrency ?? effectiveSelectedFarePrice.adult?.totalFare ?? 0),
    children: Number(effectiveSelectedFarePrice.child?.totalFareOriginalCurrency ?? effectiveSelectedFarePrice.child?.totalFare ?? 0),
    infant: Number(effectiveSelectedFarePrice.infant?.totalFareOriginalCurrency ?? effectiveSelectedFarePrice.infant?.totalFare ?? 0),
    all: Number(effectiveSelectedFarePrice.all?.totalFareOriginalCurrency ?? effectiveSelectedFarePrice.all?.totalFare ?? 0),
  }
}

export function updateFlightPaxBaggage(
  itemId: string,
  flightIndex: number,
  paxBaggageMap: App.Checkout.PaxBaggageMap,
): AppAction {
  return {
    type: CHECKOUT_UPDATE_FLIGHT_ITEM_PAX_BAGGAGE,
    itemId,
    flightIndex,
    paxBaggageMap,
  }
}

function applyComplementaryBaggageSelections(
  dispatch: AppDispatch,
  selectedFares: Array<{ flightGroups: Array<Models.Search.FlightGroup> }>,
  targetItem: App.Checkout.FlightItem,
  flightIndex: number,
) {
  const passengers = targetItem.passengers
  const flightGroupFares = selectedFares?.map(fare => fare.flightGroups[0]) || []
  const oneWay = isOneWayFlight(targetItem.fareType)

  const isComplementaryBaggageApplicable = (oneWay && flightGroupFares.length === 1) || (!oneWay && flightGroupFares.length === 2)

  if (isComplementaryBaggageApplicable) {
    const departingComplementaryQuantityMap = buildBaggageMapFromFares(flightGroupFares[0] as any)

    if (!isEmptyObject(departingComplementaryQuantityMap)) {
      const departingMap = passengers.reduce<App.Checkout.PaxBaggageMap>(
        applyTravelFusionComplementaryReducer(departingComplementaryQuantityMap), {})
      dispatch(updateFlightPaxBaggage(targetItem.itemId, flightIndex, departingMap))
    }

    if (!oneWay) {
      const returningComplementaryQuantityMap = buildBaggageMapFromFares(flightGroupFares[1] as any)
      if (!isEmptyObject(returningComplementaryQuantityMap)) {
        const returningMap = passengers.reduce<App.Checkout.PaxBaggageMap>(
          applyTravelFusionComplementaryReducer(returningComplementaryQuantityMap), {})
        dispatch(updateFlightPaxBaggage(targetItem.itemId, flightIndex, returningMap))
      }
    }
  }
}

export function getCheckoutFlightItemFinalFare(item: App.Checkout.FlightItem, itemIndex: number, additionalPayload?: AdditionalSelectFlightsPayload): AppAction {
  return function(dispatch, getState) {
    const state = getState()
    if (item.searchId && item.flights[0] && (item.fareType !== 'return' || item.flights[1])) {
      const selectedFareIds = item.flights.map(flight => flight.journeyId)

      // Create correct mapping using flight segment information instead of array indices
      const selectedUpsellOptions: Record<string, string> = {}

      if (item.flights[0]?.fareFamily?.id) {
        selectedUpsellOptions[item.flights[0].journeyId] = item.flights[0].fareFamily.id
      }

      if (item.flights[1]?.fareFamily?.id) {
        selectedUpsellOptions[item.flights[1].journeyId] = item.flights[1].fareFamily.id
      }

      // isStandalone = false when:
      // 1. item.isBundlePriced = true (Maldives charter bundle pricing)
      // 2. or item.bundledItemIds has items (legacy bundle)
      const isStandalone = !item.isBundlePriced && !item.bundledItemIds?.length
      const searchFnfEligible = getSearchFnfEligible(state, item.searchId)

      // isFnFBooking = true when the user is checking out with FnF pricing applied
      // fnfEligible alone is not reliable — we need to verify fnfPrice is actually used
      // We must pass seat-eligible passengers so the check matches the displayed price
      const seatEligiblePassengers = item.passengers.filter(p => p.type !== 'infant').length
      const searchResults = Object.values(state.flights.searchV2Flights)
      const matchingSearch = searchResults.find(search => search?.searchId === item.searchId)
      const isFnFBooking = matchingSearch?.fares?.some(fareGroup =>
        fareGroup.some(journey =>
          item.flights.some(flight =>
            flight.journeyId === journey.id &&
            isSelectedFareFnfPriceActive(journey, searchFnfEligible, seatEligiblePassengers),
          ),
        ),
      ) ?? item.isFnFBooking

      // Derive fnfPromoCodeApplicable from the original search data (not from journey objects
      // which may have been overwritten by feeder flight searches that don't return this field)
      let fnfPromoCodeApplicable = item.fnfPromoCodeApplicable ?? false
      if (matchingSearch && item.flights.length > 0) {
        fnfPromoCodeApplicable = item.flights.every(flight => {
          if (!matchingSearch.fares) {
            return false
          }
          const matchingJourney = matchingSearch.fares.flat().find(
            journey => journey.id === flight.journeyId,
          )
          if (!matchingJourney) {
            return false
          }
          return matchingJourney.flightGroup.flights.some(f => f.fnfPromoCodeApplicable)
        })
      }

      const params: FlightSelectParams = {
        journeyId: item.searchId,
        departingJourneyKey: item.flights[0].journeyKey,
        departingFareFamilyId: item.flights[0].fareFamily?.id,
        returningJourneyKey: item.fareType == 'return' ? item.flights[1]?.journeyKey : undefined,
        returningFareFamilyId: item.flights[1]?.fareFamily?.id,
        departingAirport: item.originAirportCode,
        arrivalAirport: item.destinationAirportCode,
        providerSearchId: item.flights[0].providerSearchId,
        region: state.geo.currentRegionCode,
        isStandalone,
        provider: item.flights[0].provider,
        isFlightsCredit: item.isFlightCredit,
        viewType: item.viewType,
        selectedFareIds,
        forceBundleId: item.forceBundleId,
        selectedUpsellOptions,
        additionalPayload,
        isFnFBooking,
        // Drives dynamic charter feeder markup in svc-flights /select. Absent when the
        // feeder cart item is missing (customer skipped connecting flights) → backend
        // applies the MEL self-feeder markup to charter fares.
        feederOriginAirportCode: getFeederOriginAirportCode(state),
      }

      dispatch({
        itemId: item.itemId,
        type: API_CALL,
        api: FETCH_FINAL_FLIGHT_FARES,
        request: () => selectflights(params).then(data => {
          const { priceSummary, selectedFares, selectedSeatsInformation } = data.result
          const totalFare = priceSummary?.find(price => price.type === 'total_fare')?.amount
          const otherFees = getOtherFees(
            item,
            priceSummary as Array<Price> | undefined,
          )
          otherFees.flightSelectedSeatsTotal = selectedSeatsInformation?.totalCost ?? 0

          if (totalFare) {
            applyComplementaryBaggageSelections(
              dispatch,
              selectedFares as unknown as Array<{ flightGroups: Array<Models.Search.FlightGroup> }>,
              item,
              itemIndex,
            )
          }

          if (!isFnFBooking) {
            // Match costs by journeyId to ensure correct order.
            // selectedFares order may not match item.flights order.
            const costs: Array<number> = item.flights.map(flight => {
              const matchingFare = selectedFares?.find(fare => fare.id === flight.journeyId)

              if (matchingFare) {
                return Number(matchingFare.price?.all?.totalFareOriginalCurrency ?? matchingFare.price?.all?.totalFare ?? 0)
              }

              // Fallback to original cost if no match found
              return flight.cost ?? 0
            })

            // FLY-5300: when svc-flights mutates `selectResult.selectedFares[*].price` for the
            // feeder bundle override, propagate the per-leg server prices into `fareFamily.cost`
            // so the checkout summary (which prefers `fareFamily.cost.all`) replaces the
            // pre-select local override with the authoritative server value.
            const fareFamilyCosts = item.flights.map(flight => {
              const matchingFare = selectedFares?.find(fare => fare.id === flight.journeyId)
              return getFareFamilyCostBreakdown(
                matchingFare as Fare | undefined,
                searchFnfEligible,
                seatEligiblePassengers,
              )
            })

            return {
              totalFare,
              otherFees,
              costs,
              fareFamilyCosts,
              isFnFBooking: false,
              fnfPromoCodeApplicable: false,
              journeysById: state.flights.journeysById,
            }
          }

          // Match costs by journeyId to ensure correct order.
          // selectedFares order may not match item.flights order.
          const costs: Array<number> = item.flights.map(flight => {
            const matchingFare = selectedFares?.find(fare => fare.id === flight.journeyId)
            const effectiveSelectedFareTotal = getEffectiveSelectedFareTotal(
              matchingFare as Fare | undefined,
              searchFnfEligible,
              seatEligiblePassengers,
            )

            if (matchingFare && effectiveSelectedFareTotal > 0) {
              return effectiveSelectedFareTotal
            }

            // Fallback to original cost if no match found
            return flight.cost ?? 0
          })
          const fareFamilyCosts = item.flights.map(flight => {
            const matchingFare = selectedFares?.find(fare => fare.id === flight.journeyId)
            return getFareFamilyCostBreakdown(
              matchingFare as Fare | undefined,
              searchFnfEligible,
              seatEligiblePassengers,
            )
          })

          const computedTotalFare = costs.reduce((total, amount) => total + (amount ?? 0), 0)

          return {
            totalFare: computedTotalFare,
            otherFees,
            costs,
            fareFamilyCosts,
            isFnFBooking,
            fnfPromoCodeApplicable,
            journeysById: state.flights.journeysById,
          }
        }),
      })
    }
  }
}

export function updateFlightTotalFare(
  itemId: string,
  newTotalFare: number,
  newDepartingTotalFare?: number,
  newReturningTotalFare?: number,
  newFlightLegFares?: Array<number>,
  newBookingFee?: number,
): AppAction {
  return {
    type: CHECKOUT_UPDATE_FLIGHT_TOTAL_FARE,
    itemId,
    newTotalFare,
    newDepartingTotalFare,
    newReturningTotalFare,
    newFlightLegFares,
    newBookingFee,
  }
}

export function updateCheckoutFlightCheckedBaggage(
  itemId: string,
  flightIndex: number,
  baggage: Array<Array<App.AddedBaggage>>,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const flightItem = state.checkout.cart.items.find(item => item.itemId === itemId)
    if (flightItem?.itemType === 'flight') {
      const paxBaggageMap = mapAddedBaggageToPaxMap(flightItem.passengers, baggage)

      dispatch(updateFlightPaxBaggage(itemId, flightIndex, paxBaggageMap))
    }
  }
}
